/**
 * Ported from ui-dropzone.ts
 *
 * A drag-and-drop file upload component.
 * Based on files-dropzone-element by George Raptis
 * https://github.com/georapbox/files-dropzone-element
 * MIT License
 */

import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { base } from "../../../design-system/index.ts";
import { customEvent } from "../../../util/event.ts";

// Type Definitions

export type DropzoneErrorCode =
  | "FILE_DIALOG_OPEN_FAILED"
  | "FILE_PROCESSING_FAILED";

export type DropzoneRejectionCode =
  | "TOO_MANY_FILES"
  | "FILE_TOO_LARGE"
  | "FILE_TOO_SMALL"
  | "FILE_INVALID_TYPE";

export type FileRejectionError = {
  code: DropzoneRejectionCode;
  message: string;
};

export type RejectedFile = {
  file: File;
  errors: FileRejectionError[];
};

export type DropzoneDropDetail = {
  acceptedFiles: File[];
  rejectedFiles: RejectedFile[];
};

export type DropzoneAcceptedDetail = {
  acceptedFiles: File[];
};

export type DropzoneRejectedDetail = {
  rejectedFiles: RejectedFile[];
};

export type DropzoneErrorDetail = {
  error: unknown;
  code: DropzoneErrorCode;
};

// Event Factories

export const dropEvent = customEvent<DropzoneDropDetail>("drop", {
  bubbles: true,
  composed: true,
});

export const dropAcceptedEvent = customEvent<DropzoneAcceptedDetail>(
  "drop-accepted",
  { bubbles: true, composed: true },
);

export const dropRejectedEvent = customEvent<DropzoneRejectedDetail>(
  "drop-rejected",
  { bubbles: true, composed: true },
);

export const dragEnterEvent = customEvent<void>("dragenter", {
  bubbles: true,
  composed: true,
});

export const dragOverEvent = customEvent<void>("dragover", {
  bubbles: true,
  composed: true,
});

export const dragLeaveEvent = customEvent<void>("dragleave", {
  bubbles: true,
  composed: true,
});

export const dropzoneErrorEvent = customEvent<DropzoneErrorDetail>(
  "dropzone-error",
  { bubbles: true, composed: true },
);

// Styles

const hostStyles = css`
  :host {
    display: block;
  }

  :host([disabled]) {
    pointer-events: none;
    opacity: 0.5;
  }

  :host([no-style]) .Dropzone {
    all: unset;
  }
`;

const componentStyles = css`
  .Dropzone {
    border: 2px dashed var(--border);
    border-radius: var(--radius-md);
    background-color: var(--background);
    color: var(--foreground);
    padding: var(--space-4);
    text-align: center;
    transition-property: border-color, background-color, color, box-shadow;
    transition-duration: var(--duration-fast);
    cursor: pointer;
  }

  .Dropzone:hover {
    border-color: var(--ring);
    background-color: var(--muted);
  }

  .Dropzone:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 var(--space-0_5) var(--background),
      0 0 0 var(--space-1) var(--ring);
  }

  .Dropzone[data-dragover] {
    border-color: var(--ring);
    background-color: var(--muted);
  }

  input[type="file"] {
    display: none;
  }
`;

// Component

/**
 * `<dui-dropzone>` — A drag-and-drop file upload area.
 *
 * @slot - Custom content for the dropzone area.
 *
 * @fires drop - Files dropped or selected (valid or invalid).
 * @fires drop-accepted - Files that passed validation.
 * @fires drop-rejected - Files that failed validation.
 * @fires dragenter - File dragged over the dropzone.
 * @fires dragover - File hovering over the dropzone.
 * @fires dragleave - File dragged away from the dropzone.
 * @fires dropzone-error - Internal error (e.g. file dialog failure).
 */
@customElement("dui-dropzone")
export class DuiDropzoneElement extends LitElement {
  static override styles = [base, hostStyles, componentStyles];

  static readonly ERROR_CODES: Record<DropzoneErrorCode, DropzoneErrorCode> = {
    FILE_DIALOG_OPEN_FAILED: "FILE_DIALOG_OPEN_FAILED",
    FILE_PROCESSING_FAILED: "FILE_PROCESSING_FAILED",
  };

  static readonly REJECTION_CODES: Record<
    DropzoneRejectionCode,
    DropzoneRejectionCode
  > = {
    TOO_MANY_FILES: "TOO_MANY_FILES",
    FILE_TOO_LARGE: "FILE_TOO_LARGE",
    FILE_TOO_SMALL: "FILE_TOO_SMALL",
    FILE_INVALID_TYPE: "FILE_INVALID_TYPE",
  };

  @property({ type: String, reflect: true })
  accessor accept: string | undefined;

  @property({ type: Boolean, reflect: true })
  accessor multiple = false;

  @property({ type: Boolean, reflect: true })
  accessor disabled = false;

  @property({ type: Number, reflect: true, attribute: "max-files" })
  accessor maxFiles = Infinity;

  @property({ type: Number, reflect: true, attribute: "max-size" })
  accessor maxSize = Infinity;

  @property({ type: Number, reflect: true, attribute: "min-size" })
  accessor minSize = 0;

  @property({ type: Boolean, reflect: true, attribute: "auto-focus" })
  accessor autoFocus = false;

  @property({ type: Boolean, reflect: true, attribute: "no-style" })
  accessor noStyle = false;

  @state()
  accessor #isDragOver = false;

  @query('input[type="file"]', true)
  accessor #fileInput: HTMLInputElement | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.autoFocus && !this.disabled) {
      this.updateComplete.then(() => {
        this.shadowRoot?.querySelector<HTMLElement>(".Dropzone")?.focus();
      });
    }
  }

  /** Opens the native file dialog programmatically. */
  openFileDialog(): void {
    if (this.disabled) return;

    const input =
      this.#fileInput ??
      this.shadowRoot?.querySelector<HTMLInputElement>('input[type="file"]');

    if (!input) return;

    try {
      if ("showPicker" in input) {
        (input as HTMLInputElement & { showPicker: () => void }).showPicker();
      } else {
        (input as HTMLInputElement).click();
      }
    } catch (error) {
      this.dispatchEvent(
        dropzoneErrorEvent({
          error,
          code: DuiDropzoneElement.ERROR_CODES.FILE_DIALOG_OPEN_FAILED,
        }),
      );
    }
  }

  #handleDragEnter = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled) return;

    this.#isDragOver = true;
    this.dispatchEvent(dragEnterEvent());
  };

  #handleDragOver = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled) return;

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }

    this.dispatchEvent(dragOverEvent());
  };

  #handleDragLeave = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled) return;

    this.#isDragOver = false;
    this.dispatchEvent(dragLeaveEvent());
  };

  #handleDrop = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (this.disabled) return;

    this.#isDragOver = false;

    try {
      const files = Array.from(e.dataTransfer?.files ?? []);
      this.#processFiles(files);
    } catch (error) {
      this.dispatchEvent(
        dropzoneErrorEvent({
          error,
          code: DuiDropzoneElement.ERROR_CODES.FILE_PROCESSING_FAILED,
        }),
      );
    }
  };

  #handleClick = (): void => {
    if (this.disabled) return;
    this.openFileDialog();
  };

  #handleKeyDown = (e: KeyboardEvent): void => {
    if (this.disabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.openFileDialog();
    }
  };

  #handleInputChange = (e: Event): void => {
    if (this.disabled) return;

    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    try {
      this.#processFiles(files);
    } catch (error) {
      this.dispatchEvent(
        dropzoneErrorEvent({
          error,
          code: DuiDropzoneElement.ERROR_CODES.FILE_PROCESSING_FAILED,
        }),
      );
    } finally {
      // Reset input to allow re-selecting the same file
      input.value = "";
    }
  };

  #processFiles(files: File[]): void {
    const { acceptedFiles, rejectedFiles } = this.#validateFiles(files);

    this.dispatchEvent(dropEvent({ acceptedFiles, rejectedFiles }));

    if (acceptedFiles.length > 0) {
      this.dispatchEvent(dropAcceptedEvent({ acceptedFiles }));
    }

    if (rejectedFiles.length > 0) {
      this.dispatchEvent(dropRejectedEvent({ rejectedFiles }));
    }
  }

  #validateFiles(files: File[]): {
    acceptedFiles: File[];
    rejectedFiles: RejectedFile[];
  } {
    const acceptedFiles: File[] = [];
    const rejectedFiles: RejectedFile[] = [];

    // Check total file count
    const maxAllowed = this.multiple ? this.maxFiles : 1;
    if (files.length > maxAllowed) {
      for (const file of files) {
        rejectedFiles.push({
          file,
          errors: [
            {
              code: DuiDropzoneElement.REJECTION_CODES.TOO_MANY_FILES,
              message: this.multiple
                ? `Too many files. Maximum allowed is ${this.maxFiles}.`
                : "Multiple files not allowed.",
            },
          ],
        });
      }
      return { acceptedFiles, rejectedFiles };
    }

    for (const file of files) {
      const errors: FileRejectionError[] = [];

      if (file.size > this.maxSize) {
        errors.push({
          code: DuiDropzoneElement.REJECTION_CODES.FILE_TOO_LARGE,
          message: `File is too large. Maximum size is ${this.maxSize} bytes.`,
        });
      }

      if (file.size < this.minSize) {
        errors.push({
          code: DuiDropzoneElement.REJECTION_CODES.FILE_TOO_SMALL,
          message: `File is too small. Minimum size is ${this.minSize} bytes.`,
        });
      }

      if (this.accept && !this.#isValidFileType(file)) {
        errors.push({
          code: DuiDropzoneElement.REJECTION_CODES.FILE_INVALID_TYPE,
          message: `File type not accepted. Allowed types: ${this.accept}`,
        });
      }

      if (errors.length > 0) {
        rejectedFiles.push({ file, errors });
      } else {
        acceptedFiles.push(file);
      }
    }

    return { acceptedFiles, rejectedFiles };
  }

  #isValidFileType(file: File): boolean {
    if (!this.accept) return true;

    const acceptedTypes = this.accept
      .split(",")
      .map((type) => type.trim().toLowerCase());

    for (const acceptedType of acceptedTypes) {
      if (acceptedType.startsWith(".")) {
        if (file.name.toLowerCase().endsWith(acceptedType)) {
          return true;
        }
      } else if (acceptedType.endsWith("/*")) {
        const baseType = acceptedType.slice(0, -2);
        if (file.type.toLowerCase().startsWith(baseType)) {
          return true;
        }
      } else {
        if (file.type.toLowerCase() === acceptedType) {
          return true;
        }
      }
    }

    return false;
  }

  override render(): TemplateResult {
    return html`
      <div
        class="Dropzone"
        tabindex="${this.disabled ? -1 : 0}"
        ?data-dragover="${this.#isDragOver}"
        @dragenter="${this.#handleDragEnter}"
        @dragover="${this.#handleDragOver}"
        @dragleave="${this.#handleDragLeave}"
        @drop="${this.#handleDrop}"
        @click="${this.#handleClick}"
        @keydown="${this.#handleKeyDown}"
      >
        <slot>
          <span>Drag and drop files here or click to select</span>
        </slot>
      </div>
      <input
        type="file"
        ?multiple="${this.multiple}"
        accept="${this.accept || ""}"
        ?disabled="${this.disabled}"
        @change="${this.#handleInputChange}"
        tabindex="-1"
        aria-hidden="true"
        hidden
      />
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dui-dropzone": DuiDropzoneElement;
  }
}
